<script setup lang="ts">
import { ref } from 'vue'

interface EventOption {
  id: string
  label: string
  cost?: string
  effects: string[]
  locked?: boolean
  lockReason?: string
}

const props = defineProps<{
  event?: {
    rarity: 'common' | 'rare' | 'sr'
    icon: string
    title: string
    description: string
    options: EventOption[]
  }
}>()

const emit = defineEmits<{
  (e: 'select', optionId: string): void
  (e: 'close'): void
}>()

// Demo event
const demoEvent = props.event ?? {
  rarity: 'rare' as const,
  icon: '📸',
  title: '粉丝误会',
  description: '莉奈被目击与一名男性共乘出租车。SNS 上开始有流言蜚语扩散...',
  options: [
    { id: 'clarify', label: '澄清声明', cost: '¥50,000', effects: ['消除丑闻', '好感度 -5'] },
    { id: 'silence', label: '保持沉默', cost: '免费', effects: ['丑闻 +10', '3周后消散'] },
    { id: 'leverage', label: '利用话题', cost: '💎', effects: ['人气 +20', '丑闻 +5'], locked: true, lockReason: '魅力 ≥70 解锁' },
  ],
}

const selectedOption = ref<string | null>(null)
const isAnimating = ref(false)

function handleSelect(option: EventOption) {
  if (option.locked || isAnimating.value) return
  selectedOption.value = option.id
  isAnimating.value = true
  setTimeout(() => {
    emit('select', option.id)
  }, 300)
}

const rarityConfig = {
  common: { label: '普通', class: 'text-text-muted border-text-muted' },
  rare: { label: 'RARE', class: 'text-primary border-primary' },
  sr: { label: 'SUPER RARE', class: 'text-accent-gold border-accent-gold' },
}
</script>

<template>
  <Transition name="event-fade">
    <div class="fixed inset-0 z-50 flex items-center justify-center p-4">
      <!-- 背景遮罩 -->
      <div class="absolute inset-0 bg-bg-deep bg-opacity-90 backdrop-blur-sm" @click="emit('close')"></div>

      <!-- 事件卡片 -->
      <div class="relative w-full max-w-lg">
        <!-- 稀有度标签 -->
        <div class="flex justify-center mb-4">
          <span
            class="px-4 py-1 border-2 rounded-full text-xs font-bold tracking-wider"
            :class="rarityConfig[demoEvent.rarity].class"
          >
            {{ rarityConfig[demoEvent.rarity].label }}
          </span>
        </div>

        <!-- 主卡片 -->
        <div
          class="bg-bg-surface rounded-lg border border-accent-purple border-opacity-20 shadow-modal overflow-hidden"
          :class="{ 'event-card--selected': isAnimating }"
        >
          <!-- 头部 -->
          <div class="p-6 text-center border-b border-accent-purple border-opacity-10">
            <span class="text-4xl mb-4 block">{{ demoEvent.icon }}</span>
            <h2 class="text-xl font-display font-bold text-text-primary mb-2">{{ demoEvent.title }}</h2>
            <p class="text-text-muted text-sm leading-relaxed">{{ demoEvent.description }}</p>
          </div>

          <!-- 选项区 -->
          <div class="p-4 space-y-3">
            <button
              v-for="option in demoEvent.options"
              :key="option.id"
              @click="handleSelect(option)"
              :disabled="option.locked"
              class="w-full text-left p-4 rounded-lg border transition-all"
              :class="[
                option.locked
                  ? 'bg-bg-base border-bg-elevated opacity-50 cursor-not-allowed'
                  : selectedOption === option.id
                    ? 'bg-primary bg-opacity-10 border-primary'
                    : 'bg-bg-base border-bg-elevated hover:border-accent-purple hover:bg-bg-surface'
              ]"
            >
              <div class="flex items-center justify-between mb-2">
                <span class="text-text-primary font-medium">{{ option.label }}</span>
                <span class="text-text-muted text-xs">{{ option.cost }}</span>
              </div>
              <div class="flex flex-wrap gap-2">
                <span
                  v-for="(effect, idx) in option.effects"
                  :key="idx"
                  class="text-xs px-2 py-0.5 rounded"
                  :class="effect.includes('+') ? 'text-accent-cyan bg-accent-cyan bg-opacity-20' : 'text-danger bg-danger bg-opacity-20'"
                >
                  {{ effect }}
                </span>
              </div>
              <p v-if="option.locked && option.lockReason" class="text-text-disabled text-xs mt-2">
                🔒 {{ option.lockReason }}
              </p>
            </button>
          </div>
        </div>
      </div>
    </div>
  </Transition>
</template>

<style scoped>
.event-fade-enter-active,
.event-fade-leave-active {
  transition: opacity 0.2s ease;
}
.event-fade-enter-active .bg-bg-surface,
.event-fade-leave-active .bg-bg-surface {
  transition: transform 0.3s cubic-bezier(0.16, 1, 0.3, 1);
}
.event-fade-enter-from,
.event-fade-leave-to {
  opacity: 0;
}
.event-fade-enter-from .bg-bg-surface {
  transform: translateY(20px);
}
.event-fade-leave-to .bg-bg-surface {
  transform: scale(0.95);
}

.event-card--selected {
  animation: card-select 0.3s ease forwards;
}

@keyframes card-select {
  to {
    transform: scale(0.9);
    opacity: 0;
  }
}
</style>
